import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar, Clock, MapPin, User, Plus, Filter, CheckCircle2, XCircle,
  ChevronRight, Eye, TrendingUp,
} from "lucide-react";
import { Sidebar } from "../components/Sidebar";
import { PremiumSegmented } from "../components/brand/PremiumSegmented";
import { useChainStore } from "../data/chainStore";
import { toast } from "sonner";

const PANEL_STYLE = {
  background: "linear-gradient(180deg, rgba(20,22,46,0.78) 0%, rgba(15,17,38,0.78) 100%)",
  border: "1px solid rgba(99,102,241,0.10)",
  backdropFilter: "blur(14px) saturate(140%)",
  WebkitBackdropFilter: "blur(14px) saturate(140%)",
  boxShadow: "0 1px 0 rgba(255,255,255,0.03) inset, 0 14px 30px -22px rgba(0,0,0,0.50)",
};

const _ts = (v) => (typeof v === "number" ? v : v ? Date.parse(v) : 0);
const fmtDate = (ts) => new Date(_ts(ts)).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
const fmtTime = (ts) => new Date(_ts(ts)).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });

const initialsOf = (s) => {
  if (!s) return "?";
  return String(s).trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
};

const STATUS_TONE = {
  Confirmed: { bg: "rgba(165,180,252,0.14)", border: "rgba(165,180,252,0.32)", fg: "#A5B4FC" },
  Completed: { bg: "rgba(16,185,129,0.14)",  border: "rgba(16,185,129,0.30)",  fg: "#34D399" },
  Cancelled: { bg: "rgba(244,63,94,0.14)",   border: "rgba(244,63,94,0.30)",   fg: "#FCA5A5" },
  "No-show":  { bg: "rgba(245,158,11,0.14)", border: "rgba(245,158,11,0.30)",  fg: "#FBBF24" },
};

const StatusPill = ({ status }) => {
  const s = STATUS_TONE[status] || STATUS_TONE.Confirmed;
  return (
    <span
      className="text-[9.5px] font-bold uppercase tracking-[0.14em] px-1.5 h-5 rounded inline-flex items-center"
      style={{ background: s.bg, color: s.fg, border: `1px solid ${s.border}` }}
    >
      {status}
    </span>
  );
};

const Stat = ({ label, value, sub, tone = "neutral", Icon }) => {
  const palette = {
    neutral: { value: "text-white",          sub: "text-slate-500" },
    good:    { value: "text-emerald-300",    sub: "text-emerald-400/70" },
    warn:    { value: "text-amber-300",      sub: "text-amber-300/70" },
    risk:    { value: "text-rose-300",       sub: "text-rose-300/70" },
    accent:  { value: "text-violet-200",     sub: "text-violet-300/70" },
  }[tone];
  return (
    <div className="rounded-2xl p-4" style={PANEL_STYLE}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-slate-500">
          {label}
        </span>
        {Icon && (
          <span
            className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{
              background: "rgba(99,102,241,0.10)",
              border: "1px solid rgba(99,102,241,0.18)",
              color: "#A5B4FC",
            }}
          >
            <Icon size={12} />
          </span>
        )}
      </div>
      <p className={`text-[22px] font-bold tabular-nums leading-tight ${palette.value}`}>{value}</p>
      {sub && <p className={`text-[10.5px] mt-1.5 tabular-nums ${palette.sub}`}>{sub}</p>}
    </div>
  );
};

/* ─── Schedule modal ───────────────────────────────────────────── */
const ScheduleModal = ({ open, onClose, onCreate, deals, properties, contacts }) => {
  const [dealId, setDealId] = useState("");
  const [buyerId, setBuyerId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("10:00");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setDealId(deals[0]?.id || "");
      setBuyerId("");
      const t = new Date(Date.now() + 86_400_000);
      setDate(t.toISOString().slice(0, 10));
      setTime("10:00");
    }
  }, [open, deals]);

  const submit = async (e) => {
    e.preventDefault();
    if (!dealId || !date || !time) return;
    setBusy(true);
    const when = new Date(`${date}T${time}:00`).getTime();
    try {
      await onCreate({ dealId, buyerId: buyerId || null, when, status: "Confirmed" });
      toast.success("Viewing scheduled");
      onClose();
    } catch {
      toast.error("Could not schedule viewing");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" data-testid="viewings-modal">
      <div
        onClick={onClose}
        className="absolute inset-0"
        style={{ background: "rgba(5,6,16,0.65)", backdropFilter: "blur(6px)" }}
      />
      <form
        onSubmit={submit}
        className="relative w-full max-w-md rounded-2xl p-6"
        style={{
          background: "linear-gradient(180deg, rgba(20,22,46,0.95) 0%, rgba(15,17,38,0.95) 100%)",
          border: "1px solid rgba(139,92,246,0.32)",
          boxShadow: "0 28px 60px -16px rgba(0,0,0,0.65)",
        }}
      >
        <p className="font-display text-[18px] font-bold text-white tracking-tight">Schedule viewing</p>
        <p className="text-[11.5px] text-slate-500 mt-1">Pick a deal, a buyer and a slot.</p>

        <div className="space-y-3 mt-5">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Deal</label>
            <select
              value={dealId}
              onChange={(e) => setDealId(e.target.value)}
              required
              className="w-full mt-1 px-3 h-10 rounded-lg text-[12.5px] text-white focus:outline-none focus:ring-2 focus:ring-violet-500/30"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
              data-testid="viewings-modal-deal"
            >
              {deals.map((d) => {
                const prop = properties.find((p) => p.id === d.propertyId);
                return (
                  <option key={d.id} value={d.id}>
                    {prop?.address || d.name || d.id.slice(0, 6)}
                  </option>
                );
              })}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Buyer (optional)</label>
            <select
              value={buyerId}
              onChange={(e) => setBuyerId(e.target.value)}
              className="w-full mt-1 px-3 h-10 rounded-lg text-[12.5px] text-white focus:outline-none focus:ring-2 focus:ring-violet-500/30"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
              data-testid="viewings-modal-buyer"
            >
              <option value="">Unassigned</option>
              {contacts
                .filter((c) => /buyer/i.test(c.role || ""))
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {`${c.firstName || c.first_name || ""} ${c.lastName || c.last_name || ""}`.trim() || c.email}
                  </option>
                ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="w-full mt-1 px-3 h-10 rounded-lg text-[12.5px] text-white focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                data-testid="viewings-modal-date"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Time</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                required
                className="w-full mt-1 px-3 h-10 rounded-lg text-[12.5px] text-white focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                data-testid="viewings-modal-time"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-lg text-[12px] font-semibold text-slate-300 hover:text-white"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="h-10 px-4 rounded-lg text-[12px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-50 transition-transform hover:-translate-y-0.5"
            style={{
              background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
              boxShadow: "0 8px 22px -8px rgba(99,102,241,0.55)",
            }}
            data-testid="viewings-modal-submit"
          >
            {busy ? "Scheduling…" : "Schedule"}
          </button>
        </div>
      </form>
    </div>
  );
};

/* ─── Page ─────────────────────────────────────────────────────── */
const RANGE_OPTIONS = [
  { id: "today",    label: "Today" },
  { id: "week",     label: "This week" },
  { id: "month",    label: "This month" },
  { id: "all",      label: "All" },
];

const STATUS_OPTIONS = [
  { id: "all",       label: "All statuses" },
  { id: "Confirmed", label: "Confirmed" },
  { id: "Completed", label: "Completed" },
  { id: "Cancelled", label: "Cancelled" },
];

export default function ViewingsPage({ embedded = false }) {
  const { viewings, deals, properties, contacts, addViewing, updateViewing } = useChainStore();
  const [range, setRange] = useState("week");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    const now = Date.now();
    const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
    let from = 0, to = Infinity;
    if (range === "today") {
      from = startOfDay.getTime();
      to = from + 86_400_000;
    } else if (range === "week") {
      from = now - 86_400_000 * 1; // yesterday → next 6 days
      to = now + 86_400_000 * 7;
    } else if (range === "month") {
      from = now - 86_400_000 * 7;
      to = now + 86_400_000 * 30;
    }
    return viewings
      .filter((v) => v.when >= from && v.when <= to)
      .filter((v) => status === "all" || v.status === status)
      .sort((a, b) => a.when - b.when);
  }, [viewings, range, status]);

  const stats = useMemo(() => {
    const now = Date.now();
    const upcoming = viewings.filter((v) => v.when >= now && v.status === "Confirmed").length;
    const completed = viewings.filter((v) => v.status === "Completed").length;
    const cancelled = viewings.filter((v) => v.status === "Cancelled").length;
    const total = viewings.length;
    const conversion = total === 0 ? 0 : Math.round((completed / total) * 100);
    const todayCount = viewings.filter((v) => {
      const d = new Date(v.when);
      const today = new Date();
      return d.toDateString() === today.toDateString();
    }).length;
    return { total, upcoming, completed, cancelled, conversion, todayCount };
  }, [viewings]);

  const groups = useMemo(() => {
    const out = [];
    let lastDay = null;
    for (const v of filtered) {
      const dayKey = new Date(v.when).toDateString();
      if (dayKey !== lastDay) {
        out.push({ day: dayKey, items: [] });
        lastDay = dayKey;
      }
      out[out.length - 1].items.push(v);
    }
    return out;
  }, [filtered]);

  const handleCreate = async (payload) => {
    await addViewing(payload);
  };

  const handleStatusChange = async (vid, newStatus) => {
    try {
      await updateViewing(vid, { status: newStatus });
      toast.success(`Marked as ${newStatus.toLowerCase()}`);
    } catch {
      toast.error("Could not update viewing");
    }
  };

  const Backdrop = () => (
    <span aria-hidden="true" className="absolute -top-32 left-1/4 w-[800px] h-[400px] rounded-full blur-3xl pointer-events-none"
      style={{ background: "radial-gradient(ellipse, rgba(139,92,246,0.10), transparent 70%)" }} />
  );

  const inner = (
    <>
      {!embedded && <Backdrop />}

      <div className={embedded ? "relative" : "relative px-4 sm:px-6 lg:px-10 pt-7 pb-12"}>
        {/* Header */}
        <div className={embedded ? "mb-7" : "mb-7 pl-12 md:pl-0 md:pr-48"}>
          <h1 className={embedded ? "font-display text-[22px] sm:text-[26px] font-bold tracking-[-0.025em] text-slate-900 leading-none" : "font-display text-[28px] sm:text-[34px] font-bold tracking-[-0.025em] text-white leading-none"}>
            Viewings
          </h1>
          <p className={embedded ? "text-[12.5px] text-slate-500 mt-2" : "text-[12.5px] text-slate-400 mt-2"}>
            Bookings, attendance and outcomes — every viewing in one place.
          </p>
        </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            <Stat label="Today"      value={stats.todayCount}            Icon={Calendar} tone="accent" />
            <Stat label="Upcoming"   value={stats.upcoming}              Icon={Clock} />
            <Stat label="Completed"  value={stats.completed}             Icon={CheckCircle2} tone="good" />
            <Stat label="Cancelled"  value={stats.cancelled}             Icon={XCircle}  tone="risk" />
            <Stat label="Conversion" value={`${stats.conversion}%`}      Icon={TrendingUp} tone="accent" sub="Completed / total" />
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap mb-4">
            <PremiumSegmented
              tone="dark"
              size="sm"
              autoWidth
              value={range}
              onChange={setRange}
              options={RANGE_OPTIONS.map((r) => ({ value: r.id, label: r.label }))}
              ariaLabel="Date range"
              testid="viewings-range"
            />
            <span className="inline-flex items-center gap-1.5 ml-1 text-slate-500">
              <Filter size={11} aria-hidden="true" />
            </span>
            <PremiumSegmented
              tone="dark"
              size="sm"
              autoWidth
              value={status}
              onChange={setStatus}
              options={STATUS_OPTIONS.map((s) => ({ value: s.id, label: s.label }))}
              ariaLabel="Status filter"
              testid="viewings-status"
            />
            <span className="text-[10.5px] text-slate-500 tabular-nums ml-auto">
              {filtered.length} viewing{filtered.length === 1 ? "" : "s"}
            </span>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="h-9 px-3.5 rounded-lg text-white text-[12px] font-bold inline-flex items-center gap-1.5 transition-transform hover:-translate-y-0.5"
              style={{
                background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
                boxShadow: "0 8px 22px -8px rgba(99,102,241,0.55)",
              }}
              data-testid="viewings-schedule-btn"
            >
              <Plus size={12} /> Schedule viewing
            </button>
          </div>

          {/* List */}
          {groups.length === 0 ? (
            <div className="rounded-2xl p-12 text-center" style={PANEL_STYLE} data-testid="viewings-empty">
              <span
                className="inline-flex w-12 h-12 rounded-2xl items-center justify-center mb-4"
                style={{
                  background: "rgba(99,102,241,0.14)",
                  border: "1px solid rgba(99,102,241,0.22)",
                  color: "#A5B4FC",
                }}
              >
                <Eye size={20} />
              </span>
              <p className="font-display text-[16px] font-bold text-white tracking-tight">
                No viewings in this view
              </p>
              <p className="text-[12.5px] text-slate-500 mt-2 max-w-md mx-auto leading-relaxed">
                Schedule one to start tracking attendance and conversion. Buyer feedback will land in the Deal Room.
              </p>
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="mt-5 h-10 px-4 rounded-lg text-white text-[12px] font-bold inline-flex items-center gap-1.5"
                style={{
                  background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
                  boxShadow: "0 8px 22px -8px rgba(99,102,241,0.55)",
                }}
              >
                <Plus size={12} /> Schedule first viewing
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {groups.map((g) => (
                <section key={g.day} data-testid={`viewings-day-${g.day}`}>
                  <h3 className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-3 px-1">
                    {fmtDate(new Date(g.day).getTime())}
                    <span className="ml-2 text-slate-600">· {g.items.length}</span>
                  </h3>
                  <ul className="space-y-2">
                    {g.items.map((v) => {
                      const deal = deals.find((d) => d.id === v.dealId);
                      const property = deal ? properties.find((p) => p.id === deal.propertyId) : null;
                      const buyer = v.buyerId ? contacts.find((c) => c.id === v.buyerId) : null;
                      const buyerName = buyer
                        ? `${buyer.firstName || buyer.first_name || ""} ${buyer.lastName || buyer.last_name || ""}`.trim() || buyer.email
                        : "Unassigned";
                      return (
                        <li
                          key={v.id}
                          className="rounded-2xl p-4 flex items-center gap-4 hover:bg-white/[0.02] transition-colors"
                          style={PANEL_STYLE}
                          data-testid={`viewings-item-${v.id}`}
                        >
                          <div className="w-12 h-12 rounded-xl flex flex-col items-center justify-center shrink-0"
                            style={{
                              background: "linear-gradient(135deg, rgba(139,92,246,0.20), rgba(99,102,241,0.10))",
                              border: "1px solid rgba(139,92,246,0.28)",
                            }}
                          >
                            <span className="text-[14px] font-bold text-white tabular-nums leading-none">{fmtTime(v.when)}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-[13px] font-bold text-white truncate">
                                {property?.address || deal?.name || "Untitled property"}
                              </p>
                              <StatusPill status={v.status} />
                            </div>
                            <p className="text-[11.5px] text-slate-500 mt-0.5 truncate flex items-center gap-3">
                              <span className="inline-flex items-center gap-1"><User size={10} /> {buyerName}</span>
                              {property?.postcode && <span className="inline-flex items-center gap-1"><MapPin size={10} /> {property.postcode}</span>}
                            </p>
                          </div>
                          {v.status === "Confirmed" && Date.now() > v.when - 60_000 && (
                            <div className="hidden sm:flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => handleStatusChange(v.id, "Completed")}
                                data-testid={`viewings-mark-completed-${v.id}`}
                                className="h-8 px-2.5 rounded-md text-[10.5px] font-semibold text-emerald-300 hover:bg-emerald-500/10"
                                style={{ background: "rgba(16,185,129,0.10)", border: "1px solid rgba(16,185,129,0.28)" }}
                              >
                                Mark completed
                              </button>
                              <button
                                type="button"
                                onClick={() => handleStatusChange(v.id, "Cancelled")}
                                className="h-8 px-2.5 rounded-md text-[10.5px] font-semibold text-rose-300 hover:bg-rose-500/10"
                                style={{ background: "rgba(244,63,94,0.10)", border: "1px solid rgba(244,63,94,0.28)" }}
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                          {deal && (
                            <Link
                              to={`/deal-rooms/${deal.id}`}
                              className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/[0.04] shrink-0"
                              style={{ border: "1px solid rgba(255,255,255,0.06)" }}
                              data-testid={`viewings-open-deal-${v.id}`}
                              aria-label="Open deal room"
                            >
                              <ChevronRight size={14} />
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>
      </>
  );

  if (embedded) {
    return (
      <div data-testid="viewings-page">
        {inner}
        <ScheduleModal
          open={open}
          onClose={() => setOpen(false)}
          onCreate={handleCreate}
          deals={deals.filter((d) => !d.archived)}
          properties={properties}
          contacts={contacts}
        />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen relative overflow-x-hidden"
      style={{ background: "#0A0D1A", color: "#fff" }}
      data-testid="viewings-page"
    >
      <Sidebar />
      <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out flex flex-col min-h-screen relative">
        {inner}
      </main>

      <ScheduleModal
        open={open}
        onClose={() => setOpen(false)}
        onCreate={handleCreate}
        deals={deals.filter((d) => !d.archived)}
        properties={properties}
        contacts={contacts}
      />
    </div>
  );
}
